import { useEffect, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { cx } from '../../lib/cx';

/**
 * Something to read while the model thinks.
 *
 * The wait is a few seconds of real work (see ThinkingChecklist), and the
 * checklist says what that work IS — but a client who has just handed over a
 * problem is watching a panel, and a panel that only ticks is a panel that
 * feels slow. These are the same voice as the ask box's own placeholders in
 * HomePage, for the same reason: the product is allowed to be good company.
 *
 * Deliberately never jokes about the OUTCOME — nothing here implies the
 * classification is a guess, or that the estimate is made up. It is a
 * billing decision, and undercutting it to be funny would cost more trust
 * than the joke buys. They are asides about the wait, not about the answer.
 */
const THINKING_ASIDES: readonly string[] = [
  'Reading it properly, not just the first line…',
  'Checking nobody already reported this last Tuesday…',
  'Weighing this against everything else in the queue…',
  'Doing the sums. Actual sums, not vibes…',
  'Working out if this is a quick one or a real one…',
  'Looking for the version of this we already fixed…',
  'Deciding whether we need to ask you anything at all…',
  'Trying very hard not to ask you three questions…',
  'Measuring twice so nobody has to cut twice…',
  'Weighing it against the last dozen like it…',
  'Almost there — it would rather be right than quick…',
  'Comparing notes with itself…',
];

const ASIDE_INTERVAL_MS = 3_800;

function ThinkingAside() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setIndex((current) => current + 1), ASIDE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  // Rotates rather than stopping at the end: unlike the checklist, which
  // holds on its last step because stepping backwards would read as a
  // restart, these have no order to contradict.
  return <p className="fs-11 text-faint italic">{THINKING_ASIDES[index % THINKING_ASIDES.length]}</p>;
}

/**
 * The "something is happening, and it's worth the wait" indicator.
 *
 * Shared by two moments: the ask box between Send and knowing which of the
 * three `/ask` outcomes this is, and the intake panel's `thinking` status
 * between rounds. Both can run several seconds — a bare spinner reads as
 * frozen well before that much time passes, so the phase string (from the
 * server: "Reading your request", "Checking it against existing requests"…)
 * is what tells the client the wait is doing something, not stuck.
 */
export function PhaseIndicator({ phase }: { phase: string }) {
  return (
    <div className="flex items-center gap-2 fs-12 text-muted">
      <Loader2 size={13} strokeWidth={2} className="flex-none animate-spin text-faint" aria-hidden />
      <span>{phase}</span>
    </div>
  );
}

/**
 * The same wait, shown as the list of what is being done to the request.
 *
 * A client who has just posted is waiting on the single most valuable thing
 * intake does, and a one-line spinner sells it as a delay. Naming the four
 * steps — is this already reported, what kind of work is it, how big is it —
 * is the difference between "the form is loading" and "it is being read
 * properly". This is the screen's main content while it runs, which is why
 * it gets a list rather than a line.
 *
 * Honest about what it knows: the steps and the current one both come from
 * the server (see IntakeState.phases), the completed ones really have been
 * asked for, and the last step holds rather than looping back to the first.
 * What the server cannot see is the boundary between steps inside one model
 * call, so the step advances on elapsed time — which is why nothing here
 * claims a step is *finished*, only that it has been reached.
 *
 * Degrades to the single line when a frame arrives without the list, which
 * is every frame from the plain GET rather than the stream.
 */
export function ThinkingChecklist({
  phases,
  step,
  fallback,
}: {
  readonly phases: readonly string[] | undefined;
  readonly step: number | undefined;
  readonly fallback: string;
}) {
  if (phases === undefined || phases.length === 0) {
    return (
      <div className="space-y-1.5">
        <PhaseIndicator phase={fallback} />
        <ThinkingAside />
      </div>
    );
  }

  const current = Math.min(Math.max(step ?? 0, 0), phases.length - 1);

  return (
    <>
      <ul className="space-y-1.5">
        {phases.map((label, index) => {
          const done = index < current;
          const active = index === current;
          return (
            <li key={label} className="flex items-center gap-2">
              <span className="flex h-3.5 w-3.5 flex-none items-center justify-center">
                {done ? (
                  <Check size={13} strokeWidth={2.5} className="text-success" aria-hidden />
                ) : active ? (
                  <Loader2 size={13} strokeWidth={2} className="animate-spin text-accent" aria-hidden />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-line-heavy" aria-hidden />
                )}
              </span>
              <span
                className={cx(
                  'fs-12',
                  done ? 'text-muted' : active ? 'font-semibold text-ink' : 'text-faint',
                )}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ul>
      <div className="mt-2">
        <ThinkingAside />
      </div>
    </>
  );
}
